import * as trading_signals from 'trading-signals';
import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { Theme } from '@mui/material/styles';
import { ColorMode } from 'theme';

import { HiroIndicator } from 'types/hiro';

export type IndicatorInputType =
  | 'number'
  | 'HighLow'
  | 'HighLowClose'
  | 'HighLowCloseVolume'
  | 'OpenHighLowCloseVolume';

export type IndicatorResultType =
  | 'number'
  | 'BandsResult'
  | 'DMAResult'
  | 'MACDResult'
  | 'LinearRegressionResult'
  | 'StochasticResult';

export type PanePlacement = 'Bottom' | 'Main';

export interface IndicatorConstructorInput {
  paramName: string;
  defaultValue?: unknown;
  validate?: (hiroIndicator: HiroIndicator) => undefined | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputTransform?: (input: number) => any;
}

// a labeled color a charting series
export interface IndicatorOutput {
  name: string;
  defaultColor: (theme: Theme) => string;
}
export interface IndicatorDefinition {
  name: string;
  description: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cls: new (...args: any[]) => unknown;
  inputType: IndicatorInputType;
  resultType: IndicatorResultType;
  constructorInputs: IndicatorConstructorInput[];

  // location of indicator
  panePlacement: PanePlacement;

  indicatorOutputs?: IndicatorOutput[];
}

const indicators: IndicatorDefinition[] = [
  {
    name: 'Acceleration Bands (ABANDS)',
    panePlacement: 'Main',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          Acceleration bands created by Price Headley are set as an envelope
          around a moving average. The upper and lower bands are of equal
          distance from the middle band.
        </p>
        <p>
          Two consecutive closes outside Acceleration Bands suggest an entry
          point in the direction of the breakout (either bullish or bearish). A
          long position is usually kept till the first close back inside the
          bands.
        </p>
      </Box>
    ),
    cls: trading_signals.AccelerationBands,
    inputType: 'HighLowClose',
    resultType: 'BandsResult',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 20 },
      { paramName: 'Width', defaultValue: 4 },
      { paramName: 'Smoothing Indicator', defaultValue: trading_signals.SMA },
    ],
  },
  {
    name: 'Accelerator Oscillator (AC)',
    panePlacement: 'Bottom',
    description: (
      <p>
        The Accelerator Oscillator (AC) is an indicator used to detect when a
        momentum changes. It has been developed by Bill Williams. If the
        momentum in an uptrend is starting to slow down, that could suggest that
        there is less interest in the asset. This typically leads to selling. In
        the inverse, momentum to the downside will start to slow down before buy
        orders come in. The Accelerator Oscillator also looks at whether there
        is an acceleration in the change of momentum.
      </p>
    ),
    cls: trading_signals.AC,
    inputType: 'HighLow',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Short AO', defaultValue: 5 },
      { paramName: 'Long AO', defaultValue: 34 },
      { paramName: 'Signal Interval', defaultValue: 5 },
    ],
  },
  {
    name: 'Average Directional Index (ADX)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The ADX was developed by John Welles Wilder (Jr.). It is a lagging
          indicator; that is, a trend must have established itself before the
          ADX will generate a signal that a trend is under way.
        </p>
        <p>
          ADX will range between 0 and 100 which makes it an oscillator. It is a
          smoothed average of the Directional Movement Index (DMI / DX).
        </p>
        <p>
          Generally, ADX readings below 20 indicate trend weakness, and readings
          above 40 indicate trend strength. A strong trend is indicated by
          readings above 50. ADX values of 75-100 signal an extremely strong
          trend.
        </p>
        <p>
          <strong>Interpretation:</strong> If ADX increases, it means that
          volatility is increasing and indicating the beginning of a new trend.
          If ADX decreases, it means that volatility is decreasing, and the
          current trend is slowing down and may even reverse. When +DI is above
          -DI, then there is more upward pressure than downward pressure in the
          market.
        </p>
        <p>
          <strong>Note:</strong> The ADX calculation relies on the DX becoming
          stable before producing meaningful results. For an Interval of 5, at
          least 9 candles are required. The first 5 candles are used to
          stabilize the DX, which then generates the initial ADX value. The
          subsequent 4 candles produce additional ADX values, allowing it to
          stabilize with 5 values for an Interval of 5.
        </p>
      </Box>
    ),
    cls: trading_signals.ADX,
    inputType: 'HighLowClose',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 14 },
      { paramName: 'Smoothing Indicator', defaultValue: trading_signals.WSMA },
    ],
  },
  {
    name: 'Average True Range (ATR)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The ATR was developed by John Welles Wilder (Jr.). The idea of ranges
          is that they show the commitment or enthusiasm of traders. Large or
          increasing ranges suggest traders prepared to continue to bid up or
          sell down a stock through the course of the day. Decreasing range
          indicates declining interest.
        </p>
        <p>
          A stock with a higher ATR is indicative of increased volatility, while
          a lower ATR suggests decreased volatility during the assessed time
          frame.
        </p>
        <ul>
          <li>
            <strong>Low ATR (e.g., 0.5 to 1):</strong> Typically associated with
            low-volatility stocks or markets. Prices tend to move in a
            relatively calm and steady manner.
          </li>
          <li>
            <strong>Moderate ATR (e.g., 1 to 2):</strong> Indicates moderate
            volatility. Prices may experience periodic fluctuations, but they
            are not extreme. Many traders find stocks with ATR around 2 to be
            suitable for trading with manageable risk.
          </li>
          <li>
            <strong>High ATR (e.g., 2 or higher):</strong> Suggests higher
            volatility. Stocks with ATR values greater than 2 are prone to more
            significant price swings, and they may exhibit larger price
            movements.
          </li>
        </ul>
      </Box>
    ),
    cls: trading_signals.ATR,
    inputType: 'HighLowClose',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 14 },
      { paramName: 'Smoothing Indicator', defaultValue: trading_signals.WSMA },
    ],
  },
  {
    name: 'Awesome Oscillator (AO)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Awesome Oscillator (AO) is an indicator used to measure market
          momentum. It has been developed by the technical analyst and charting
          enthusiast Bill Williams.
        </p>
        <p>
          When AO crosses above Zero, short term momentum is rising faster than
          long term momentum which signals a bullish buying opportunity.
        </p>
        <p>
          When AO crosses below Zero, short term momentum is falling faster then
          the long term momentum which signals a bearish selling opportunity.
        </p>
      </Box>
    ),
    cls: trading_signals.AO,
    inputType: 'HighLow',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Short Interval', defaultValue: 5 },
      {
        paramName: 'Long Interval',
        validate: (hiroIndicator: HiroIndicator): undefined | string => {
          const shortIntervalVal = hiroIndicator.constructorInputs?.[0];
          if ((shortIntervalVal?.length ?? 0) == 0) {
            return;
          }

          const longIntervalVal = hiroIndicator.constructorInputs?.[1];
          if ((longIntervalVal?.length ?? 0) == 0) {
            return;
          }

          if (parseInt(shortIntervalVal!) > parseInt(longIntervalVal!)) {
            return `'Short Interval' must be smaller than 'Long Interval'`;
          }
        },
        defaultValue: 34,
      },
      { paramName: 'Smoothing Indicator', defaultValue: trading_signals.SMA },
    ],
  },
  {
    name: 'Bollinger Bands (BBANDS)',
    panePlacement: 'Main',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          Bollinger Bands (BBANDS), developed by John A. Bollinger, are set as
          an envelope around a moving average. Narrow bands indicate a sideways
          trend (ranging markets). To determine a breakout direction,{' '}
          <a
            href="https://www.investopedia.com/articles/technical/04/030304.asp"
            target="_blank"
            rel="noopener noreferrer"
          >
            Investopia.com suggests
          </a>{' '}
          to use the relative strength index (RSI) along with one or two
          volume-based indicators such as the intraday intensity index
          (developed by David Bostian) or the accumulation/distribution index
          (developed by Larry William).
        </p>
        <p>
          When the upper and lower bands expand, there can be "M" and "W"
          formations. The "W" formation indicates a bullish movement and the "M"
          formation indicates a bearish movement.
        </p>
      </Box>
    ),
    cls: trading_signals.BollingerBands,
    inputType: 'number',
    resultType: 'BandsResult',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 20 },
      { paramName: 'Deviation Multiplier', defaultValue: 2 },
    ],
  },
  {
    name: 'Bollinger Bands Width (BBW)',
    panePlacement: 'Bottom',
    description: (
      <p>
        The Bollinger Bands Width (BBW) indicator, developed by John A.
        Bollinger, merges the information of Bollinger Bands into one definite
        number. It defines the narrowness of the underlying Bollinger Bands by
        representing the difference between the Upper Band and the Lower Band.
      </p>
    ),
    cls: trading_signals.BollingerBandsWidth,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Bollinger Bands' }],
  },
  {
    name: 'Center of Gravity (CG)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          Implementation of the Center of Gravity (CG) oscillator by John
          Ehlers. The Center of Gravity (CG) aims to identify turning points in
          price action with minimal lag (leading indicator). Peaks and troughs
          in CG can precede actual price highs and lows. The CG is often paired
          with its own signal line for entry/exit triggers.
        </p>
        <p>
          <strong>Interpretation:</strong> Crossing the zero line may suggest a
          shift in trend.
        </p>
        <p>
          <strong>Note:</strong>
        </p>
        <ul>
          <li>
            According to the specification, the price inputs shall be calculated
            the following way: <code>((High Price + Low Price) / 2)</code>
          </li>
          <li>
            The selected Interval should be half the dominant cycle length
            (signal line)
          </li>
          <li>
            If the Interval gets too short, the CG oscillator loses its
            smoothing and gets a little too nervous for profitable trading
          </li>
        </ul>
      </Box>
    ),
    cls: trading_signals.CG,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 10 },
      { paramName: 'Signal Interval', defaultValue: 3 },
    ],
  },
  {
    name: 'Commodity Channel Index (CCI)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Commodity Channel Index (CCI), developed by Donald Lambert in
          1980, compares the current mean price with the average mean price over
          a period of time. Approximately 70 to 80 percent of CCI values are
          between −100 and +100, which makes it an oscillator. Values above +100
          imply an overbought condition, while values below −100 imply an
          oversold condition.
        </p>
        <p>
          According to{' '}
          <a
            href="https://www.investopedia.com/articles/active-trading/031914/how-traders-can-utilize-cci-commodity-channel-index-trade-stock-trends.asp#multiple-timeframe-cci-strategy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Investopia.com
          </a>
          , traders often buy when the CCI dips below -100 and then rallies back
          above -100 to sell the security when it moves above +100 and then
          drops back below +100.
        </p>
        <p>
          <strong>Interpretation:</strong>
        </p>
        <ul>
          <li>
            -100 and below: Indicates an oversold condition or the start of a
            strong downtrend.
          </li>
          <li>
            +100 and above: Indicates an overbought condition or the start of a
            strong uptrend.
          </li>
          <li>Values near 0 often signal a lack of clear momentum.</li>
        </ul>
        <p>
          <strong>Note:</strong> Traders often combine CCI with other indicators
          to confirm trends or signals, as using it alone can lead to false
          signals. It's particularly useful in volatile markets or when
          identifying shorter-term trading opportunities.
        </p>
      </Box>
    ),
    cls: trading_signals.CCI,
    inputType: 'HighLowClose',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 20 }],
  },
  {
    name: 'Directional Movement Index (DX)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The DX was developed by John Welles Wilder (Jr.). and may help traders
          assess the strength of a trend (momentum) and direction of the trend.
        </p>
        <p>
          If there is no change in the trend, then the DX is <code>0</code>. The
          return value increases when there is a stronger trend (either negative
          or positive). To detect if the trend is bullish or bearish you have to
          compare +DI and -DI. When +DI is above -DI, then there is more upward
          pressure than downward pressure in the market.
        </p>
      </Box>
    ),
    cls: trading_signals.DX,
    inputType: 'HighLowClose',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 14 },
      { paramName: 'Smoothing Indicator', defaultValue: trading_signals.WSMA },
    ],
  },
  {
    name: 'Double Exponential Moving Average (DEMA)',
    panePlacement: 'Main',
    description: (
      <p>
        The Double Exponential Moving Average (DEMA) was developed by Patrick G.
        Mulloy. It attempts to remove the lag associated with Moving Averages by
        placing more weight on recent values. It has its name because the value
        of an EMA is doubled which makes it responds more quickly to short-term
        price changes than a normal EMA.
      </p>
    ),
    cls: trading_signals.DEMA,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 9 }],
  },
  {
    name: 'Dual Moving Average (DMA)',
    panePlacement: 'Main',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The DMA consists of two moving averages: Short-term &amp; long-term.
        </p>
        <p>
          <strong>Dual Moving Average Crossover:</strong> A short-term MA
          crossing above a long-term MA indicates a bullish buying opportunity.
          A short-term MA crossing below a long-term MA indicates a bearish
          selling opportunity.
        </p>
      </Box>
    ),
    cls: trading_signals.DMA,
    inputType: 'number',
    resultType: 'DMAResult',
    constructorInputs: [
      { paramName: 'Short', defaultValue: 5 },
      { paramName: 'Long', defaultValue: 10 },
      { paramName: 'Smoothing Indicator', defaultValue: trading_signals.SMA },
    ],
  },
  {
    name: 'Exponential Moving Average (EMA)',
    panePlacement: 'Main',
    description: (
      <p>
        Compared to SMA, the EMA puts more emphasis on the recent prices to
        reduce lag. Due to its responsiveness to price changes, it rises faster
        and falls faster than the SMA when the price is inclining or declining.
      </p>
    ),
    cls: trading_signals.EMA,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 9 }],
  },
  {
    name: 'Interquartile Range (IQR)',
    panePlacement: 'Bottom',
    description: (
      <p>
        The IQR is the difference between the 75th percentile (Q3) and the 25th
        percentile (Q1) of a data set. It is a measure of statistical dispersion
        and is robust to outliers.
      </p>
    ),
    cls: trading_signals.IQR,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 14 }],
  },
  {
    name: 'Linear Regression (LINREG)',
    panePlacement: 'Main',
    description: (
      <p>
        It fits a straight line to price data over a chosen period using the
        least-squares method. The slope of the line shows the trend direction
        and strength. It's similar in use to moving averages but mathematically
        more precise, since it minimizes the squared distance between price
        points and the fitted line.
      </p>
    ),
    cls: trading_signals.LinearRegression,
    inputType: 'number',
    resultType: 'LinearRegressionResult',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 5 }],
  },
  {
    name: 'Mean Absolute Deviation (MAD)',
    panePlacement: 'Bottom',
    description: (
      <p>
        The mean absolute deviation (MAD) is calculating the absolute deviation
        / difference from the mean over a period. Large outliers will reflect in
        a higher MAD.
      </p>
    ),
    cls: trading_signals.MAD,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 14 }],
  },
  {
    name: 'Momentum (MOM / MTM)',
    panePlacement: 'Bottom',
    description: (
      <p>
        The Momentum indicator returns the change between the current price and
        the price n times ago.
      </p>
    ),
    cls: trading_signals.MOM,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 10 }],
  },
  {
    name: 'Moving Average Convergence Divergence (MACD)',
    panePlacement: 'Bottom',
    description: (
      <p>
        The MACD triggers trading signals when it crosses above (bullish buying
        opportunity) or below (bearish selling opportunity) its signal line.
        MACD can be used together with the RSI to provide a more accurate
        trading signal.
      </p>
    ),
    cls: trading_signals.MACD,
    inputType: 'number',
    resultType: 'MACDResult',
    constructorInputs: [
      {
        paramName: 'Short',
        defaultValue: 12,
        inputTransform: (input: number) => new trading_signals.EMA(input),
      },
      {
        paramName: 'Long',
        defaultValue: 26,
        inputTransform: (input: number) => new trading_signals.EMA(input),
      },
      {
        paramName: 'Signal',
        defaultValue: 9,
        inputTransform: (input: number) => new trading_signals.EMA(input),
      },
    ],

    indicatorOutputs: [
      {
        name: 'Histogram',
        defaultColor: (theme: Theme) => theme.palette.gray,
      },
      {
        name: 'MACD',
        defaultColor: (_theme: Theme) => '#ff0000',
      },
      {
        name: 'Signal',
        defaultColor: (theme: Theme) =>
          theme.colorMode === ColorMode.LIGHT ? '#0000ff' : '#4040ee',
      },
    ],
  },
  {
    name: 'On-Balance Volume (OBV)',
    panePlacement: 'Bottom',
    description: (
      <p>
        On-balance volume (OBV) is a technical trading momentum indicator that
        uses volume flow to predict changes in stock price. Joseph Granville
        first developed the OBV metric in the 1963 book Granville's New Key to
        Stock Market Profits.
      </p>
    ),
    cls: trading_signals.OBV,
    inputType: 'OpenHighLowCloseVolume',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 20 }],
  },
  {
    name: 'Parabolic SAR (PSAR)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Parabolic SAR (Stop and Reverse) is a technical indicator used in
          trading to determine the direction of an asset's price and potential
          points of trend reversal. It was developed by J. Welles Wilder Jr.,
          who also created indicators like the RSI.
        </p>
        <p>
          <strong>Interpretation:</strong> The indicator places dots above or
          below the price. If the dots are below the price, it signals an
          uptrend. If the dots are above the price it signals a downtrend. It
          "stops and reverses" when the trend is likely to change, hence the
          name. Its logic says to stay in a trend as long as the dots stay on
          the same side of the price. Exit or reverse positions when the dots
          flip to the opposite side.
        </p>
        <p>
          <strong>Note:</strong> It's particularly useful in trending markets,
          but less reliable in sideways or choppy markets.
        </p>
      </Box>
    ),
    cls: trading_signals.PSAR,
    inputType: 'HighLow',
    resultType: 'number',
    constructorInputs: [
      {
        paramName: 'Config',
        defaultValue: { accelerationStep: 0.02, accelerationMax: 0.2 },
      },
    ],
  },
  {
    name: 'Range Expansion Index (REI)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Range Expansion Index (REI) is a momentum oscillator, measuring
          the velocity and magnitude of directional price movements. Developed
          by Thomas DeMark, it compares the current day's range to the average
          range over a given period. It quantifies whether the current price
          range represents a contraction or expansion compared to the average.
          The REI is most typically used on an 8 day timeframe. Extreme REI
          values often signal potential reversal points, as they reflect sharp
          directional moves that may not be sustainable.
        </p>
        <p>
          <strong>Interpretation:</strong> According to Thomas DeMark, potential
          shifts in momentum when the REI rises above +60 and then drops below
          (price weakness). Conversely, when it falls below -60 and then climbs
          back above, it may signal price strength.
        </p>
        <ul>
          <li>
            <strong>REI &gt; +60:</strong> Overbought condition — strong upward
            momentum that may be unsustainable (when crossed from above)
          </li>
          <li>
            <strong>REI between +60 and -60:</strong> Neutral zone — the market
            is neither gaining upward strength nor showing downside pressure
          </li>
          <li>
            <strong>REI &lt; -60:</strong> Oversold condition — strong downward
            momentum that could reverse (when crossed from below)
          </li>
        </ul>
      </Box>
    ),
    cls: trading_signals.REI,
    inputType: 'HighLowClose',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 8 }],
  },
  {
    name: 'Rate-of-Change (ROC)',
    panePlacement: 'Bottom',
    description: (
      <p>
        A positive Rate of Change (ROC) signals a high momentum and a positive
        trend. A decreasing ROC or even negative ROC indicates a downtrend.
      </p>
    ),
    cls: trading_signals.ROC,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 10 }],
  },
  {
    name: 'Relative Moving Average (RMA)',
    panePlacement: 'Main',
    description: (
      <p>
        Use RMA to identify bullish or bearish trends. It provides a smoother
        curve compared to SMA and EMA, reacting more slowly to price changes.
      </p>
    ),
    cls: trading_signals.RMA,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 14 }],
  },
  {
    name: 'Relative Strength Index (RSI)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Relative Strength Index (RSI) is an oscillator that ranges between
          0 and 100. The RSI can be used to find trend reversals, i.e. when a
          downtrend doesn't generate a RSI below 30 and rallies above 70 it
          could mean that a trend reversal to the upside is taking place. Trend
          lines and moving averages should be used to validate such statements.
        </p>
        <p>
          The RSI is mostly useful in markets that alternate between bullish and
          bearish movements.
        </p>
        <p>
          <strong>Interpretation:</strong> A RSI value of 30 or below indicates
          an oversold condition (not a good time to sell because there is an
          oversupply). A RSI value of 70 or above indicates an overbought
          condition (sell high opportunity because market may correct the price
          in the near future).
        </p>
      </Box>
    ),
    cls: trading_signals.RSI,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 14 },
      { paramName: 'Smoothing Indicator', defaultValue: trading_signals.RMA },
    ],
  },
  {
    name: 'Simple Moving Average (SMA)',
    panePlacement: 'Main',
    description: (
      <p>
        The Simple Moving Average (SMA) creates an average of all prices within
        a fixed Interval. The SMA weights the prices of all periods equally
        which makes it not as responsive to recent prices as the EMA.
      </p>
    ),
    cls: trading_signals.SMA,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 9 }],
  },
  {
    name: "Spencer's 15-Point Moving Average (SMA15)",
    panePlacement: 'Main',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          A specialized weighted moving average that uses 15 points with
          predefined weights designed for optimal data smoothing. It's a
          specific weighted moving average, designed to preserve the trend
          component in data while minimizing distortion from seasonal or
          irregular variations.
        </p>
        <p>
          The formula uses these specific weights:{' '}
          <code>
            [-3, -6, -5, 3, 21, 46, 67, 74, 67, 46, 21, 3, -5, -6, -3]
          </code>
        </p>
      </Box>
    ),
    cls: trading_signals.SMA15,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 15 }],
  },
  {
    name: 'Stochastic Oscillator (STOCH)',
    panePlacement: 'Main',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Stochastic Oscillator was developed by George Lane and is
          range-bound between 0 and 100. The Stochastic Oscillator attempts to
          predict price turning points. A value of 80 indicates that the asset
          is on the verge of being overbought. By default, a Simple Moving
          Average (SMA) is used. When the momentum starts to slow down, the
          Stochastic Oscillator values start to turn down. In the case of an
          uptrend, prices tend to make higher highs, and the settlement price
          usually tends to be in the upper end of that time period's trading
          range.
        </p>
        <p>
          The stochastic k (%k) values represent the relation between current
          close to the period's price range (high/low). It is sometimes referred
          as the "fast" stochastic period (fastk). The stochastic d (%d) values
          represent a Moving Average of the %k values. It is sometimes referred
          as the "slow" period.
        </p>
      </Box>
    ),
    cls: trading_signals.StochasticOscillator,
    inputType: 'HighLowClose',
    resultType: 'StochasticResult',
    constructorInputs: [
      { paramName: 'n', defaultValue: 5 },
      { paramName: 'm', defaultValue: 10 },
      { paramName: 'p', defaultValue: 4 },
    ],
  },
  {
    name: 'Stochastic RSI (STOCHRSI)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Stochastic RSI is an oscillator ranging from 0 to 1 and was
          developed by Tushar S. Chande and Stanley Kroll. Compared to the RSI,
          the Stochastic RSI is much steeper and often resides at the extremes
          (0 or 1). It can be used to identify short-term trends.
        </p>
        <ul>
          <li>
            A return value of 0.2 or below indicates an oversold condition
          </li>
          <li>A return value around 0.5 indicates a neutral market</li>
          <li>
            A return value of 0.8 or above indicates an overbought condition
          </li>
          <li>
            Overbought doesn't mean that the price will reverse lower but it
            shows that the RSI reached an extreme
          </li>
          <li>
            Oversold doesn't mean that the price will reverse higher but it
            shows that the RSI reached an extreme
          </li>
        </ul>
        <p>
          The Stochastic RSI is often read through crossovers of its %K (fast)
          and %D (signal) lines. A %K crossing above %D in the oversold zone
          (below 20) may suggest a buy, while a %K crossing below %D in the
          overbought zone (above 80) can signal a potential sell.
        </p>
      </Box>
    ),
    cls: trading_signals.StochasticRSI,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Interval', defaultValue: 14 },
      { paramName: 'Smoothing RSI', defaultValue: trading_signals.RMA },
    ],
  },
  {
    name: "Tom Demark's Sequential Indicator (TDS)",
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The TD Sequential indicator is used to identify potential turning
          points in the price of an asset. It consists of two phases: TD Setup
          and TD Countdown. This implementation focuses on the TD Setup phase,
          which is the most commonly used part for trend exhaustion signals.
        </p>
        <ul>
          <li>
            A bullish setup occurs when there are 9 consecutive closes greater
            than the close 4 bars earlier. A possible sell opportunity is when
            the low of bars 6 and 7 in the count are exceeded by the low of bars
            8 or 9.
          </li>
          <li>
            A bearish setup occurs when there are 9 consecutive closes less than
            the close 4 bars earlier. A possible buy opportunity is when the low
            of bars 6 and 7 in the count are exceeded by the low of bars 8 or 9.
          </li>
        </ul>
      </Box>
    ),
    cls: trading_signals.TDS,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [],
  },
  {
    name: 'True Range (TR)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The True Range (TR) was developed by John Welles Wilder (Jr.). The
          range (R) is a candle's highest price minus it's lowest price. The
          true range extends it to yesterday's closing price if it was outside
          of the current range.
        </p>
        <p>
          Low return values indicate a sideways trend with little volatility.
        </p>
      </Box>
    ),
    cls: trading_signals.TR,
    inputType: 'HighLowClose',
    resultType: 'number',
    constructorInputs: [],
  },
  {
    name: 'Volume-Weighted Average Price (VWAP)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Volume-Weighted Average Price (VWAP) represents the average price
          a security has traded at throughout the day, weighted by both volume
          and price.
        </p>
        <p>
          <strong>Formula:</strong>{' '}
          <code>VWAP = (Sum of (Price × Volume)) / Total Volume</code>
        </p>
      </Box>
    ),
    cls: trading_signals.VWAP,
    inputType: 'HighLowCloseVolume',
    resultType: 'number',
    constructorInputs: [],
  },
  {
    name: 'Weighted Moving Average (WMA)',
    panePlacement: 'Main',
    description: (
      <p>
        Compared to SMA, the WMA puts more emphasis on the recent prices to
        reduce lag. Due to its responsiveness to price changes, it rises faster
        and falls faster than the SMA when the price is inclining or declining.
      </p>
    ),
    cls: trading_signals.WMA,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 9 }],
  },
  {
    name: "Wilder's Smoothed Moving Average (WSMA / WWS / SMMA / MEMA)",
    panePlacement: 'Main',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          Developed by John Welles Wilder (Jr.) to help identifying and spotting
          bullish and bearish trends. Similar to Exponential Moving Averages
          with the difference that a smoothing factor of 1/Interval is being
          used, which makes it respond more slowly to price changes.
        </p>
        <p>
          <strong>Synonyms:</strong>
        </p>
        <ul>
          <li>Modified Exponential Moving Average (MEMA)</li>
          <li>Smoothed Moving Average (SMMA)</li>
          <li>Welles Wilder's Smoothing (WWS)</li>
        </ul>
      </Box>
    ),
    cls: trading_signals.WSMA,
    inputType: 'number',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 14 }],
  },
  {
    name: 'Williams %R (WILLR)',
    panePlacement: 'Bottom',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The Williams %R indicator, developed by Larry Williams, is a momentum
          indicator that measures overbought and oversold levels. It is similar
          to the Stochastic Oscillator but is plotted on an inverted scale,
          ranging from 0 to -100. Readings from 0 to -20 are considered
          overbought, while readings from -80 to -100 are considered oversold.
        </p>
        <p>
          The Williams %R is arithmetically exactly equivalent to the %K
          stochastic oscillator, mirrored at the 0%-line.
        </p>
        <p>
          <strong>Formula:</strong>{' '}
          <code>
            %R = (Highest High - Close) / (Highest High - Lowest Low) × -100
          </code>
        </p>
      </Box>
    ),
    cls: trading_signals.WilliamsR,
    inputType: 'HighLowClose',
    resultType: 'number',
    constructorInputs: [{ paramName: 'Interval', defaultValue: 14 }],
  },
  {
    name: 'Zig Zag Indicator (ZigZag)',
    panePlacement: 'Main',
    description: (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p>
          The ZigZag indicator is a technical analysis tool used to identify
          price trends by filtering out smaller price movements. It works by
          identifying significant highs and lows in a price series and drawing
          lines between them. For a high or low to be considered significant,
          the price must reverse by at least a specified percentage (deviation)
          from the last extreme point.
        </p>
        <p>
          The indicator alternates between tracking highs and lows: after
          confirming a high, it searches for a significant low, and after
          confirming a low, it searches for a significant high.
        </p>
        <p>
          The Zig Zag indicator is considered to be a very lagging indicator
          because its values are plotted only after each time period closes, and
          it only forms a permanent new line once the price has moved
          significantly. Traders can use popular technical indicators like RSI,
          ADX, and the Stochastics oscillator to confirm the price of a security
          is overbought or oversold when the ZigZag line changes direction.
        </p>
      </Box>
    ),
    cls: trading_signals.ZigZag,
    inputType: 'HighLow',
    resultType: 'number',
    constructorInputs: [
      { paramName: 'Config', defaultValue: { deviation: 5 } },
    ],
  },
];

export const indicatorFromName = new Map(indicators.map((i) => [i.name, i]));
export const availableIndicators = indicators.filter((i) =>
  ['number', 'BandsResult', 'MACDResult'].includes(i.resultType),
);

export const hiroIndicatorValid = (hiroIndicator: HiroIndicator): boolean => {
  if ((hiroIndicator.indicatorName?.length ?? 0) == 0) {
    return false;
  }
  const indicatorConstructorInputs = getConstructorInputs(
    hiroIndicator.indicatorName!,
  )!;
  return (
    !(hiroIndicator.deleting ?? false) &&
    hiroIndicator.symbolLineParams != null &&
    (hiroIndicator?.constructorInputs?.map((i) => i != null).length ?? 0) >=
      (indicatorFromName
        .get(hiroIndicator.indicatorName!)
        ?.constructorInputs.filter(({ defaultValue }) => defaultValue == null)
        ?.length ?? -1) &&
    (hiroIndicator?.constructorInputs?.filter(
      (_, idx) =>
        (indicatorConstructorInputs?.[idx]?.validate?.(hiroIndicator)?.length ??
          0) > 0,
    ).length ?? 0) == 0
  );
};

export const getConstructorInputs = (
  indicatorName: string,
): undefined | IndicatorConstructorInput[] => {
  let indicatorNameToGet = indicatorName;
  if (indicatorName == 'Bollinger Bands Width (BBW)') {
    indicatorNameToGet = 'Bollinger Bands (BBANDS)';
  }
  return indicatorFromName.get(indicatorNameToGet)?.constructorInputs;
};
